import { setLocalMicOn, setLocalVideoOn } from "@/store/slices/callSlice";
import {
  useEffect,
  useState,
  type ChangeEvent,
  type FormEvent,
  type JSX,
} from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

interface RatingsState {
  knowledge: number;
  quality: number;
  experience: number;
}

export default function FeedBackFormCamera() {
  const [issueResolved, setIssueResolved] = useState<string>("");
  const [ratings, setRatings] = useState<RatingsState>({
    knowledge: 0,
    quality: 0,
    experience: 0,
  });
  const [timeLeft, setTimeLeft] = useState(35);
  const navigator = useNavigate();
  const dispatch = useDispatch()

  useEffect(() => {
    if (timeLeft <= 0) {
      navigator("/ThankYou");
      return;
    }

    const timer = setTimeout(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft, navigator]);

  const handleRadioChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setIssueResolved(event.target.value);
  };

  const handleStarClick = (
    category: keyof RatingsState,
    rating: number
  ): void => {
    setRatings((prevRatings) => ({
      ...prevRatings,
      [category]: rating,
    }));
  };

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    console.log("Form submitted:", { issueResolved, ratings });
    navigator("/ThankYou");
    toast.success("Thank you for your feedback! Check the console for submitted data.");
  };
  useEffect(() => {
    const hasReloaded = localStorage.getItem("feedbackPageReloaded");
    dispatch(setLocalMicOn(false))
    dispatch(setLocalVideoOn(false))
    if (!hasReloaded) {
      // Mark that we've reloaded once
      localStorage.setItem("feedbackPageReloaded", "true");
      // Reload the page
      window.location.reload();
    } else {
      // Clear the flag for next time the page is opened fresh
      localStorage.removeItem("feedbackPageReloaded");
    }
  }, []);

  const renderStars = (category: keyof RatingsState): JSX.Element => {
    return (
      <div className="flex space-x-1 sm:space-x-2">
        {[...Array(5)].map((_, index) => {
          const ratingValue = index + 1;
          return (
            <label key={index}>
              <input
                type="radio"
                name={category}
                value={ratingValue}
                onClick={() => handleStarClick(category, ratingValue)}
                className="hidden"
              />
              <svg
                className="cursor-pointer transition-transform duration-200 transform hover:scale-110"
                fill={
                  ratingValue <= ratings[category] ? "#ffc107" : "#e0e0e0"
                }
                width="28"
                height="28"
                viewBox="0 0 24 24"
              >
                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
              </svg>
            </label>
          );
        })}
      </div>
    );
  };

  return (
    <div className="bg-gray-50 min-h-fit flex justify-center font-sans p-4 ">
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-4 sm:p-6 transition-transform duration-300 hover:scale-[1.01]">
        {/* Countdown timer */}
        <div className="absolute md:top-4 md:right-4 top-2 right-2 bg-[#13a8e1] text-white text-xs sm:text-sm font-semibold rounded-full px-3 sm:px-4 py-1 shadow-md">
          00:{timeLeft.toString().padStart(2, "0")}
        </div>

        {/* Title */}
        <h1 className="text-2xl sm:text-3xl font-bold text-center text-gray-800 mb-2 mt-2">
          Your Feedback Matters to Us!
        </h1>
        <p className="text-center text-blue-600 mb-3 sm:mb-4 font-medium text-sm sm:text-base">
          Please rate our services for the following parameters:
        </p>

        {/* Radio question */}
        <div className="mb-3 bg-white p-4 sm:p-4 rounded-xl border border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0 transition-shadow duration-300 hover:shadow-md">
          <p className="text-gray-700 font-medium text-sm sm:text-base text-center sm:text-left">
            Was your issue resolved?
          </p>
          <div className="flex items-center justify-center sm:justify-end space-x-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="issueResolved"
                value="Yes"
                checked={issueResolved === "Yes"}
                onChange={handleRadioChange}
                className="form-radio text-blue-500 h-5 w-5"
              />
              <span className="ml-2 text-gray-600">Yes</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="issueResolved"
                value="No"
                checked={issueResolved === "No"}
                onChange={handleRadioChange}
                className="form-radio text-blue-500 h-5 w-5"
              />
              <span className="ml-2 text-gray-600">No</span>
            </label>
          </div>
        </div>

        {/* Subheading */}
        <p className="text-blue-600 text-center mb-2 sm:mb-3 font-medium text-sm sm:text-base">
          How was your experience in the following areas of our customer service:
        </p>

        {/* Ratings sections */}
        <div className="space-y-4">
          <div className="bg-white p-3 sm:p-4 rounded-xl border border-gray-200 flex flex-col sm:flex-row sm:items-center items-center sm:justify-between space-y-3 sm:space-y-0 transition-shadow duration-300 hover:shadow-md">
            <p className="text-gray-700 font-medium text-sm sm:text-base text-center sm:text-left">
              Agent's knowledge and skill
            </p>
            {renderStars("knowledge")}
          </div>

          <div className="bg-white p-3 sm:p-4 rounded-xl border border-gray-200 flex flex-col sm:flex-row sm:items-center items-center sm:justify-between space-y-3 sm:space-y-0 transition-shadow duration-300 hover:shadow-md">
            <p className="text-gray-700 font-medium text-sm sm:text-base text-center sm:text-left">
              Quality of video and audio
            </p>
            {renderStars("quality")}
          </div>

          <div className="bg-white p-3 sm:p-4 rounded-xl border border-gray-200 flex flex-col sm:flex-row sm:items-center items-center sm:justify-between space-y-3 sm:space-y-0 transition-shadow duration-300 hover:shadow-md">
            <p className="text-gray-700 font-medium text-sm sm:text-base text-center sm:text-left">
              Rate your overall experience
            </p>
            {renderStars("experience")}
          </div>
        </div>

        {/* Submit button */}
        <div className="text-center mt-5 sm:mt-6">
          <button
            onClick={handleSubmit}
            className="bg-[#13a8e1] text-white text-sm sm:text-base font-bold py-3 px-10 rounded-full shadow-lg transition-transform duration-200 hover:scale-105 hover:bg-[#0f91c3] w-full sm:w-auto"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}
